/**
 * Who is looking at a page that both the dashboard and the public site render (the meeting page): a member
 * inside /dashboard, or anyone on the public meeting page (/meeting/<id>), signed in or not. Fixed for the page
 * load (main.tsx sets it before the first render), so it is a plain module value rather than a store.
 *
 * Links: inside the dashboard they stay in the dashboard; on the public page they go to the public site's pages,
 * and to nothing when the public site has no page for it (a private speaker).
 */
import { seatPage } from "./site";

export interface Viewer {
  /** The public meeting page (/meeting/<id>), not /dashboard. */
  publicPage: boolean;
  /** Has dashboard access: can follow, take notes, annotate, Ask. */
  admitted: boolean;
  signedIn: boolean;
}

export const DASHBOARD_VIEWER: Viewer = { publicPage: false, admitted: true, signedIn: true };
let viewer: Viewer = DASHBOARD_VIEWER;

export const setViewer = (v: Viewer) => { viewer = v; };
export const getViewer = (): Viewer => viewer;

/** Signed out: sign in and come back here. Signed in without dashboard access: the dashboard's access screen. */
export function signInToAct() {
  location.href = viewer.signedIn ? "/dashboard" : `/signin?next=${encodeURIComponent(location.pathname + location.search)}`;
}

const enc = encodeURIComponent;

export const meetingHref = (id: string) => (viewer.publicPage ? `/meeting/${enc(id)}` : `/dashboard/meetings/${enc(id)}`);

/**
 * A record number (O2025-0021035) or matter id. Public: /matter/<record>, which the server answers with a 301 to the
 * readable address (/matter/<record>-<slug>, site/js/matter-url.js): the snapshot's titles are short display titles, not
 * the official title the slug comes from. The page's server-rendered copy for search engines links the readable address.
 */
export const legislationHref = (id: string) => (viewer.publicPage ? `/matter/${enc(id)}` : `/dashboard/legislation/${enc(id)}`);

/** A person's profile. Public: a sitting member's seat page ("a:<seat>": /ward/<n> in Chicago, /district/<n> on another
 *  city's site, app/site.ts); anyone else has none. */
export function personHref(id: string): string | undefined {
  if (!viewer.publicPage) return `/dashboard/people/${enc(id)}`;
  const seat = /^a:(\d{1,2})$/.exec(id)?.[1];
  return seat ? seatPage(Number(seat)) : undefined;
}
